import {
  AutoComplete,
  AutoCompleteCreatable,
  AutoCompleteInput,
  AutoCompleteItem,
  AutoCompleteList,
} from '@choc-ui/chakra-autocomplete';
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  SimpleGrid,
  Stack,
} from '@chakra-ui/react';
import {
  Button,
  DefaultNumberInputField,
  Heading,
  ImageList,
  InputField,
} from '~/common/components/defaultUIComponents';
import DepartmentApi from '~/common/api/zeiterfassungServerApi/DepartmentApi';
import { Department } from '~/common/entities/Department/DepartmentTypes';
import ProjectApi from '~/common/api/zeiterfassungServerApi/ProjectApi';
import useTranslate from '~/common/utils/translate';
import * as ReactRouter from 'react-router-dom';
import { Form, Formik, useField } from 'formik';
import { useCreateEditJobForm } from './utils';
import { useMemo, useState } from 'react';
import * as yup from 'yup';
import _ from 'lodash';

export const CreateEditJobScreen: React.FC = () => {
  const navigate = ReactRouter.useNavigate();
  const translate = useTranslate();
  const params = ReactRouter.useParams<{ id?: string; project?: string }>();

  const {
    initialValues,
    submit,
    job,
    departments = [],
    setImages,
    initialImages,
    projectJobs,
    rotatePicture,
  } = useCreateEditJobForm(params.id, params.project);

  const [{ data: project }] = ProjectApi.hooks.useProjectQuery(
    _.first(job?.projects),
  );

  const createEditJobSchreenSchema = yup.object({
    name: yup
      .string()
      .min(5, 'validator.error.minLength5')
      .required('validator.required'),
    hours: yup.number().optional(),
    minutes: yup.number().optional(),
    seconds: yup.number().optional(),
    description: yup.string().optional(),
    step_number: yup
      .number()
      .positive('validator.error.notNegative')
      .min(1, 'validator.error.minOne')
      .required(),
    important_message: yup.string().optional(),
    department: yup.number().required('validator.required'),
  });

  return (
    <Flex direction="column" h="full" position="relative">
      <Formik
        initialValues={initialValues}
        onSubmit={submit}
        validationSchema={createEditJobSchreenSchema}
        enableReinitialize
      >
        {({ dirty, isSubmitting, isValid, setFieldValue }) => {
          ReactRouter.unstable_usePrompt({
            when: dirty,
            message: translate('createOrder.leaveScreenWarning') as string,
          });
          return (
            <Form style={{ height: '100%' }}>
              <Grid
                templateColumns="repeat(3, 1fr)"
                overflow="hidden"
                height="full"
              >
                <GridItem
                  colSpan={2}
                  ml={5}
                  mr={5}
                  sx={{
                    height: '88vh',
                    mt: '2.5vh',
                    mb: '2.5vh',
                    overflowY: 'auto',
                  }}
                >
                  <Stack justify="space-between" h="full">
                    <Box>
                      <Heading>
                        {job
                          ? translate('editJob.heading', [
                              project?.name as string,
                            ])
                          : 'createJob.heading'}
                      </Heading>
                      <InputField
                        name="name"
                        label="createJob.name.label"
                        mb={5}
                      />
                      <SimpleGrid columns={3} columnGap={2}>
                        <DefaultNumberInputField
                          name="hours"
                          label="createJob.durationInHours.label"
                          mb={2}
                          min={0}
                        />
                        <DefaultNumberInputField
                          name="minutes"
                          label="createJob.durationInMinutes.label"
                          mb={2}
                          min={0}
                          maxInputLenght={2}
                        />
                        <DefaultNumberInputField
                          name="seconds"
                          label="createJob.durationInSeconds.label"
                          mb={2}
                          min={0}
                          maxInputLenght={2}
                        />
                      </SimpleGrid>
                      <JobDepartments
                        label="createJob.department.label"
                        name="department"
                        departments={departments}
                        onChange={(value) => {
                          const department = _.find(
                            departments,
                            (d) => d.id === value,
                          );
                          const latestStepNumber =
                            _.last(
                              _.filter(
                                _.orderBy(projectJobs, 'step_number'),
                                (pj) => pj.department?.id === department?.id,
                              ),
                            )?.step_number ?? initialValues.step_number - 1;
                          void setFieldValue(
                            'step_number',
                            latestStepNumber + 1,
                          );
                        }}
                      />
                      <InputField
                        name="description"
                        label="createJob.description.label"
                        textarea
                        mb={5}
                      />
                      <SimpleGrid columns={2} columnGap={3}>
                        <DefaultNumberInputField
                          name="step_number"
                          label="createJob.step_number.label"
                          min={1}
                          w="full"
                        />
                        <DefaultNumberInputField
                          name="done_per_project"
                          label="createJob.done_per_project.label"
                          min={1}
                          w="full"
                        />
                      </SimpleGrid>
                      <InputField
                        name="important_message"
                        label="job.important_message.label"
                      />
                    </Box>
                    <Box>
                      <Button
                        type="submit"
                        isLoading={isSubmitting}
                        isDisabled={!isValid}
                        w="full"
                        mb={2}
                      >
                        {!job ? 'createJob.submit.text' : 'editJob.submit.text'}
                      </Button>
                      <Button w="full" onClick={() => navigate(-1)}>
                        cancelButton.text
                      </Button>
                    </Box>
                  </Stack>
                </GridItem>
                <GridItem
                  borderLeft="1px solid black"
                  sx={{
                    height: '88vh',
                    mt: '2.5vh',
                    mb: '2.5vh',
                    overflowY: 'auto',
                    pl: '3px',
                  }}
                >
                  <ImageList
                    initialImages={initialImages}
                    onChange={setImages}
                    onRotate={rotatePicture}
                  />
                </GridItem>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </Flex>
  );
};

type JobDepartmentProps = {
  name: string;
  onChange?: (departmentId: number) => void;
  label?: string;
  isDisabled?: boolean;
  departments: Department[];
};

function JobDepartments({
  name,
  label,
  isDisabled,
  onChange,
  departments,
}: JobDepartmentProps) {
  const { mutateAsync: createDepartment } =
    DepartmentApi.hooks.useCreateDepartment();
  const [field, meta, helpers] = useField<number | undefined>(name);
  const departmentName = useMemo(
    () => departments?.find((it) => it.id === field.value)?.name ?? '',
    [field.value],
  );
  const t = useTranslate();
  const [fieldValue, setFieldValue] = useState<string>();
  const departmentExists = useMemo(
    () =>
      fieldValue &&
      departments.find((it) =>
        it.name.toLowerCase().includes(fieldValue?.toLowerCase()),
      ),
    [fieldValue, departments],
  );

  return (
    <FormControl isDisabled={isDisabled} isInvalid={!!meta.error}>
      <FormLabel>{t(label)}</FormLabel>
      <AutoComplete
        rollNavigation
        openOnFocus
        suggestWhenEmpty
        focusInputOnSelect
        creatable
        onCreateOption={({ item }) =>
          void createDepartment({
            name: item.value as string,
            description: '',
          }).then((department) => helpers.setValue(department.id))
        }
        onBlur={() => void field.onBlur({ target: { name } })}
        value={departmentName}
        filter={(query: string, _value: string, optionLabel: string) => {
          return optionLabel.toLowerCase().includes(query.toLowerCase());
        }}
        listAllValuesOnFocus
        onChange={(value: string) => {
          const departmentId = Number(value);
          void helpers.setValue(departmentId);
          if (onChange) onChange(departmentId);
          setFieldValue(undefined);
        }}
      >
        <Box
          sx={{
            borderColor: '#807a7a',
            borderWidth: '1px',
            p: '1px',
            borderRadius: 'md',
            '&:hover': {
              borderColor: 'gray.300',
            },
            '&:focus-within': {
              borderColor: 'green.600',
              borderWidth: '2px',
              p: '0px',
            },
          }}
        >
          <AutoCompleteInput
            variant="filled"
            value={fieldValue ?? departmentName}
            onChange={(e) => setFieldValue(e.target.value)}
            onBlur={() => setFieldValue(undefined)}
            sx={{
              bg: 'inherit',
              '&:hover': {
                bg: 'inherit',
              },
            }}
          />
        </Box>
        <AutoCompleteList>
          {_.orderBy(departments, ['name']).map((department, index) => (
            <AutoCompleteItem
              key={`department-complete-option-${index}`}
              value={department.id}
              label={department.name}
            >
              {department.name}
            </AutoCompleteItem>
          ))}
          {!departmentExists && (
            <AutoCompleteCreatable>
              {fieldValue} {t('create')}
            </AutoCompleteCreatable>
          )}
        </AutoCompleteList>
      </AutoComplete>
      {!!meta.error && <FormErrorMessage>{t(meta.error)}</FormErrorMessage>}
    </FormControl>
  );
}
